<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  agent: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(['close', 'saved']);

const { t } = useI18n();
const getters = useStoreGetters();

const accountId = computed(() => getters.getCurrentAccountId.value);

const isLoading = ref(false);
const isSaving = ref(false);

const allCalendars = ref([]);
const allProducts = ref([]);

const form = ref({
  isProfessional: false,
  active: true,
  defaultCalendarId: null,
  specialty: '',
  description: '',
  calendarIds: [],
  productIds: [],
  calendarPermissions: [],
});

const accessLevels = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
  { value: 'manager', label: 'Manager' },
];

const calendarOptions = computed(() =>
  (allCalendars.value || []).map(calendar => ({
    value: calendar.id,
    label: calendar.name,
  }))
);

const productOptions = computed(() =>
  (allProducts.value || []).map(product => ({
    value: product.id,
    label: `${product.name} (${product.product_kind})`,
  }))
);

const selectedPermissionCalendarIds = computed({
  get() {
    return (form.value.calendarPermissions || []).map(item => Number(item.calendar_id));
  },
  set(ids) {
    const idSet = new Set((ids || []).map(Number));
    const next = [];

    idSet.forEach(calendarId => {
      const existing = (form.value.calendarPermissions || []).find(
        item => Number(item.calendar_id) === Number(calendarId)
      );
      next.push({
        calendar_id: Number(calendarId),
        access_level: existing?.access_level || 'viewer',
      });
    });

    form.value.calendarPermissions = next;
  },
});

const selectedPermissionCalendars = computed(() => {
  const byId = new Map((allCalendars.value || []).map(c => [Number(c.id), c]));
  return (form.value.calendarPermissions || [])
    .map(permission => {
      const calendar = byId.get(Number(permission.calendar_id));
      if (!calendar) return null;
      return {
        id: Number(permission.calendar_id),
        name: calendar.name,
        accessLevel: permission.access_level || 'viewer',
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
});

const loadSettings = async () => {
  if (!props.agent?.id) return;

  isLoading.value = true;
  try {
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/agents/${props.agent.id}/professional_settings`
    );

    const data = response.data || {};
    const profile = data.profile || {};

    allCalendars.value = data.all_calendars || [];
    allProducts.value = data.all_products || [];

    form.value = {
      isProfessional: !!profile.is_professional,
      active: profile.active !== false,
      defaultCalendarId: profile.default_calendar_id || null,
      specialty: profile.specialty || '',
      description: profile.description || '',
      calendarIds: profile.calendar_ids || [],
      productIds: profile.product_ids || [],
      calendarPermissions: profile.calendar_permissions || [],
    };
  } catch (error) {
    useAlert(t('AGENT_PROFESSIONAL.TOAST.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.agent?.id,
  () => {
    loadSettings();
  },
  { immediate: true }
);

watch(
  () => form.value.isProfessional,
  isProfessional => {
    if (!isProfessional) {
      form.value.defaultCalendarId = null;
      form.value.calendarIds = [];
    }
  }
);

const updateCalendarPermission = (calendarId, accessLevel) => {
  form.value.calendarPermissions = (form.value.calendarPermissions || []).map(item => {
    if (Number(item.calendar_id) !== Number(calendarId)) return item;
    return { ...item, access_level: accessLevel };
  });
};

const save = async () => {
  if (!props.agent?.id) return;

  isSaving.value = true;
  try {
    const payload = {
      professional_settings: {
        is_professional: !!form.value.isProfessional,
        active: !!form.value.active,
        default_calendar_id: form.value.isProfessional ? form.value.defaultCalendarId || null : null,
        specialty: form.value.specialty || null,
        description: form.value.description || null,
        calendar_ids: form.value.isProfessional ? form.value.calendarIds : [],
        product_ids: form.value.isProfessional ? form.value.productIds : [],
        calendar_permissions: form.value.calendarPermissions,
      },
    };

    await window.axios.patch(
      `/api/v1/accounts/${accountId.value}/agents/${props.agent.id}/professional_settings`,
      payload
    );

    useAlert(t('AGENT_PROFESSIONAL.TOAST.SAVE_SUCCESS'));
    emit('saved');
    emit('close');
  } catch (error) {
    const message =
      error?.response?.data?.errors?.join(', ') ||
      error?.response?.data?.error ||
      t('AGENT_PROFESSIONAL.TOAST.SAVE_ERROR');
    useAlert(message);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="t('AGENT_PROFESSIONAL.MODAL_TITLE', { name: agent?.name || '' })"
      :header-content="t('AGENT_PROFESSIONAL.MODAL_DESC')"
    />

    <div v-if="isLoading" class="p-6 text-sm text-n-slate-11">{{ t('AGENT_PROFESSIONAL.LOADING') }}</div>

    <div v-else class="p-4 space-y-5">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
          <input v-model="form.isProfessional" type="checkbox" />
          {{ t('AGENT_PROFESSIONAL.IS_PROFESSIONAL_LABEL') }}
        </label>

        <label class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
          <input v-model="form.active" type="checkbox" />
          {{ t('AGENT_PROFESSIONAL.ACTIVE_LABEL') }}
        </label>
      </div>

      <div v-if="form.isProfessional" class="space-y-4">
        <div>
          <label class="text-sm font-medium text-n-slate-12 block mb-1">{{ t('AGENT_PROFESSIONAL.SPECIALTY_LABEL') }}</label>
          <input
            v-model="form.specialty"
            type="text"
            class="w-full"
            :placeholder="t('AGENT_PROFESSIONAL.SPECIALTY_PLACEHOLDER')"
          />
        </div>

        <div>
          <label class="text-sm font-medium text-n-slate-12 block mb-1">{{ t('AGENT_PROFESSIONAL.DESCRIPTION_LABEL') }}</label>
          <textarea
            v-model="form.description"
            rows="3"
            class="w-full"
            :placeholder="t('AGENT_PROFESSIONAL.DESCRIPTION_PLACEHOLDER')"
          />
        </div>

        <div>
          <label class="text-sm font-medium text-n-slate-12 block mb-1">{{ t('AGENT_PROFESSIONAL.DEFAULT_CALENDAR_LABEL') }}</label>
          <select v-model="form.defaultCalendarId" class="w-full">
            <option :value="null">{{ t('AGENT_PROFESSIONAL.NO_DEFAULT_CALENDAR') }}</option>
            <option
              v-for="calendar in allCalendars"
              :key="calendar.id"
              :value="calendar.id"
            >
              {{ calendar.name }}
            </option>
          </select>
        </div>

        <div>
          <label class="text-sm font-medium text-n-slate-12 block mb-2">{{ t('AGENT_PROFESSIONAL.CALENDARS_LABEL') }}</label>
          <TagMultiSelectComboBox
            v-model="form.calendarIds"
            :options="calendarOptions"
            :placeholder="t('AGENT_PROFESSIONAL.CALENDARS_PLACEHOLDER')"
            :search-placeholder="t('AGENT_PROFESSIONAL.CALENDARS_SEARCH')"
            :empty-state="t('AGENT_PROFESSIONAL.CALENDARS_EMPTY')"
            use-fixed-dropdown
          />
        </div>
      </div>

      <div v-if="form.isProfessional">
        <label class="text-sm font-medium text-n-slate-12 block mb-2">{{ t('AGENT_PROFESSIONAL.PRODUCTS_LABEL') }}</label>
        <TagMultiSelectComboBox
          v-model="form.productIds"
          :options="productOptions"
          :placeholder="t('AGENT_PROFESSIONAL.PRODUCTS_PLACEHOLDER')"
          :search-placeholder="t('AGENT_PROFESSIONAL.PRODUCTS_SEARCH')"
          :empty-state="t('AGENT_PROFESSIONAL.PRODUCTS_EMPTY')"
          use-fixed-dropdown
        />
      </div>

      <div class="space-y-3">
        <label class="text-sm font-medium text-n-slate-12 block">
          {{ t('AGENT_PROFESSIONAL.PERMISSIONS_LABEL') }}
        </label>

        <TagMultiSelectComboBox
          v-model="selectedPermissionCalendarIds"
          :options="calendarOptions"
          :placeholder="t('AGENT_PROFESSIONAL.PERMISSIONS_PLACEHOLDER')"
          :search-placeholder="t('AGENT_PROFESSIONAL.CALENDARS_SEARCH')"
          :empty-state="t('AGENT_PROFESSIONAL.CALENDARS_EMPTY')"
          use-fixed-dropdown
        />

        <div
          v-if="selectedPermissionCalendars.length"
          class="max-h-64 overflow-y-auto border border-n-weak rounded-lg p-2 space-y-2"
        >
          <div
            v-for="calendar in selectedPermissionCalendars"
            :key="calendar.id"
            class="flex items-center gap-2"
          >
            <div class="flex-1 min-w-0">
              <div class="text-sm truncate">{{ calendar.name }}</div>
            </div>
            <select
              :value="calendar.accessLevel"
              class="w-32"
              @change="updateCalendarPermission(calendar.id, $event.target.value)"
            >
              <option v-for="level in accessLevels" :key="level.value" :value="level.value">
                {{ level.label }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="flex justify-end items-center gap-2 pt-2">
        <Button faded slate :label="t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')" @click="emit('close')" />
        <Button :label="isSaving ? t('AGENT_PROFESSIONAL.SAVING') : t('AGENT_PROFESSIONAL.SAVE_BUTTON')" :is-loading="isSaving" :disabled="isSaving" @click="save" />
      </div>
    </div>
  </div>
</template>
